import { IconPlayTriangle } from '@leland-dev/leland-ui-library';
import React, { useEffect, useState } from 'react';

import phog from '../../utils/analytics/posthog/posthog';
import YouTubeEmbed from '../common/YouTubeEmbed';

import {
  type IntroVideoCoachFragment,
  type IntroVideoVideoLinkFragment,
} from './__generated-gql-types__/IntroVideo.generated';
import { useIntroVideoModal } from './modals/IntroVideoModal';

interface IntroVideoProps {
  className?: string;
  videoClassName?: string;
  video: IntroVideoVideoLinkFragment;
  coach: IntroVideoCoachFragment;
  categorySlug: Possible<string>;
  compact?: boolean;
}

const IntroVideo: React.FC<IntroVideoProps> = ({
  className,
  videoClassName,
  video,
  coach,
  categorySlug,
  compact,
}) => {
  const { setOpen: setIntroVideoModalOpen, Modal: IntroVideoModal } =
    useIntroVideoModal();

  const [showYouTubeEmbed, setShowYouTubeEmbed] = useState(false);

  useEffect(() => {
    if (showYouTubeEmbed) {
      phog.capture('coach_profile--intro_video_view', {
        coachSlug: coach.slug,
        ...(categorySlug ? { categorySlug } : {}),
      });
    }
  }, [showYouTubeEmbed, coach.slug, categorySlug]);

  const imageBgStyle: Pick<React.CSSProperties, 'backgroundImage'> = {
    backgroundImage: `linear-gradient(to top, var(--tw-gradient-stops)), url(${video.thumbnailLink})`,
  };

  const playButton = (
    <div
      className={`flex ${
        compact ? 'size-8' : 'size-10'
      } shrink-0 items-center justify-center rounded-full bg-white/40 group-hover:bg-white/60`}
    >
      <IconPlayTriangle
        className={`${compact ? 'size-4' : 'size-5'} text-white`}
      />
    </div>
  );

  const videoInfo = (
    <div className="flex flex-col items-start justify-center">
      <h2
        className={`line-clamp-1 font-medium ${
          compact
            ? 'leading-snug text-leland-gray-dark'
            : 'text-lg leading-6 text-white'
        }`}
      >
        Get to know {coach.user.firstName}
      </h2>
      <p
        className={`font-normal ${
          compact
            ? 'leading-snug text-leland-gray-light'
            : 'text-sm text-white opacity-80'
        }`}
      >
        {video.duration}
      </p>
    </div>
  );

  return compact ? (
    <>
      <button
        className={`group flex items-center space-x-4 rounded-xl bg-leland-gray-hover p-3 ${className}`}
        onClick={() => setIntroVideoModalOpen(true)}
        id="intro_video--play_video"
      >
        <div
          className="flex h-16 w-28 shrink-0 items-end rounded-md from-black/40 bg-cover bg-center bg-no-repeat p-2 shadow-inner group-hover:from-black/60"
          style={imageBgStyle}
        >
          {playButton}
        </div>
        {videoInfo}
      </button>
      <IntroVideoModal youtubeId={video.youtubeId} />
    </>
  ) : (
    <div className={`w-full ${className}`}>
      {showYouTubeEmbed ? (
        <YouTubeEmbed
          className={`size-full rounded-lg ${videoClassName}`}
          youtubeId={video.youtubeId}
        />
      ) : (
        <button
          className={`group flex size-full items-end justify-start rounded-lg from-black/40 bg-cover bg-center bg-no-repeat p-4 shadow-inner hover:from-black/60 ${videoClassName}`}
          style={imageBgStyle}
          onClick={() => setShowYouTubeEmbed(true)}
          id="intro_video--play_video"
        >
          <div className="flex items-center justify-start space-x-2">
            {playButton}
            {videoInfo}
          </div>
        </button>
      )}
    </div>
  );
};

export default IntroVideo;
